"use client";

import { useEffect } from "react";
import { doc, onSnapshot } from "firebase/firestore";

import { firestore } from "@/lib/firebase";
import { client } from "@/lib/hono";
import type {
    LomdaContent,
    LomdaScene,
} from "@/features/lomdaEditorV2/lib/lomdaTypes";

interface UseRenderAudioSyncArgs {
    lomdaId: string | undefined;
    activeRenderRunId: string | null;
    onAudioRefreshed: (
        scenes: { id: string; audio: NonNullable<LomdaScene["audio"]> }[]
    ) => void;
    onRenderTerminated: () => void;
}

/**
 * Subscribe to the in-flight render's progress doc. When the engine flips
 * `data.audioPersisted = true`, fetch the lomda's content and merge fresh
 * `scene.audio` into in-memory state — so the editor reflects the engine's
 * R2 uploads without waiting for the user to refresh. Terminal progress
 * (100 or -1) clears the active runId.
 */
export const useRenderAudioSync = ({
    lomdaId,
    activeRenderRunId,
    onAudioRefreshed,
    onRenderTerminated,
}: UseRenderAudioSyncArgs) => {
    useEffect(() => {
        if (!activeRenderRunId || !lomdaId) return;

        let consumed = false;

        const unsubscribe = onSnapshot(
            doc(
                firestore,
                "ai-generations",
                `lomdaV2Render-${activeRenderRunId}`
            ),
            async (snapshot) => {
                const snapData = snapshot.data();
                if (!snapData) return;
                const progress = snapData.progress;
                const audioPersisted = snapData.data?.audioPersisted === true;

                if (audioPersisted && !consumed) {
                    consumed = true;
                    try {
                        const res = await client.api.storage["lomda-v2"][
                            ":lomdaId"
                        ].$get({ param: { lomdaId } });
                        if (!res.ok) return;
                        const body = await res.json();
                        const contentStr = body?.data?.content;
                        if (typeof contentStr !== "string") return;
                        const parsed = JSON.parse(contentStr) as LomdaContent;
                        const refreshed = parsed.scenes
                            .filter((s) => s.audio?.url)
                            .map((s) => ({
                                id: s.id,
                                audio: s.audio!,
                            }));
                        onAudioRefreshed(refreshed);
                    } catch (err) {
                        console.error(
                            "[useRenderAudioSync] failed to refresh audio",
                            err
                        );
                    }
                }

                if (typeof progress === "number" && (progress >= 100 || progress < 0)) {
                    onRenderTerminated();
                }
            },
            (err) => {
                console.error(
                    "[useRenderAudioSync] firestore listener error",
                    err
                );
            }
        );

        return () => unsubscribe();
    }, [activeRenderRunId, lomdaId, onAudioRefreshed, onRenderTerminated]);
};
